import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { FeedResponse } from '@news-assist/shared';

interface UseFeedOptions {
  source?: string;
  story?: string;
  type?: 'all' | 'claims' | 'quotes';
  claimType?: string;
  recency?: 'all' | 'breaking' | 'developing' | 'context';
  multiSourceOnly?: boolean;
  groupByStory?: boolean;
  since?: string;
  urgency?: string;       // comma-separated urgency levels (e.g., "1,2,3")
  sortByUrgency?: string; // "true" to sort by urgency
}

// Map UI recency terms to API freshness terms
const recencyToFreshness = (recency?: string): string | undefined => {
  if (!recency || recency === 'all') return undefined;
  if (recency === 'developing') return 'recent';
  if (recency === 'context') return 'background';
  return recency; // 'breaking' stays as-is
};

export function useFeed(options: UseFeedOptions = {}) {
  return useInfiniteQuery({
    queryKey: ['feed', options],
    queryFn: async ({ pageParam }) => {
      // Use pageParam for pagination cursor, but respect options.since as a floor
      const sinceParam = pageParam || options.since;
      return apiClient.get<FeedResponse>('/feed', {
        limit: 50,
        since: sinceParam,
        source: options.source,
        story: options.story,
        type: options.type,
        claimType: options.claimType,
        freshness: recencyToFreshness(options.recency),
        multiSourceOnly: options.multiSourceOnly ? 'true' : undefined,
        groupByStory: options.groupByStory ? 'true' : undefined,
        urgency: options.urgency,
        sortByUrgency: options.sortByUrgency,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    refetchInterval: 30000, // Poll every 30 seconds
  });
}
